/**
 * Interfaces de las tablas de la base de datos. La lista viva esta en `TABLAS`.
 *
 * Convenciones normativas:
 *
 *   - Todo campo monetario lleva el sufijo `Centavos` y es entero (Req. 10.8).
 *   - Todo campo de cantidad lleva el sufijo `Base` y esta en la unidad base
 *     del insumo: g, ml o pza (Req. 5.3).
 *   - Las fechas de negocio son `FechaLocal` ('AAAA-MM-DD'), sin hora ni zona
 *     horaria (Req. 12.10).
 *   - `creadoEn` y similares son marcas de tiempo ISO completas, porque
 *     representan instantes y no dias de calendario.
 *
 * Las tablas marcadas HISTORIAL INMUTABLE solo se escriben una vez. Las
 * correcciones generan registros compensatorios (Req. 18.1, 18.2).
 */

import type { Centavos } from '@/domain/money';
import type { FechaLocal, DiaSemana } from '@/domain/dates';
import type { Dimension, UnidadBase, FactorEquivalencia } from '@/domain/units';
import type { EstadoPedido, EstadoPago, EstadoEntrega } from '@/domain/orders';
import type { TipoMovimientoCaja } from '@/domain/cash';

/**
 * Version del CONTENIDO de las tablas, distinta de la version de Dexie.
 *
 * 1 -> 2: se agrego la tabla `orders` (pedidos de clientes).
 *
 * Al subirla hay que registrar la migracion correspondiente en `migrarDatos`
 * (src/db/backup-format.ts), o ningun respaldo anterior se podra importar.
 */
export const VERSION_ESQUEMA = 3;

// ---------------------------------------------------------------------------
// 1. Configuracion
// ---------------------------------------------------------------------------

export interface Settings {
  /** Registro unico. Siempre 1. */
  id: 1;
  schemaVersion: number;
  moneda: string;
  /** Dias de anticipacion predeterminados para preparar. Valor inicial: 1 */
  diasAnticipacionDefault: number;
  /** 1 = las semanas empiezan en lunes, 0 = en domingo */
  primerDiaSemana: 0 | 1;
  /** ISO del ultimo respaldo exportado con exito. null si nunca se ha hecho. */
  ultimoRespaldoEn: string | null;
  /** Dias sin respaldo tras los cuales se muestra el aviso. Valor inicial: 7 */
  diasAvisoRespaldo: number;
  /** Resultado de navigator.storage.persist(), para diagnostico. */
  almacenamientoPersistente: boolean | null;
  creadoEn: string;
}

// ---------------------------------------------------------------------------
// 2. Insumos e inventario
// ---------------------------------------------------------------------------

export interface Ingredient {
  id: string;
  nombre: string;
  categoria: string | null;
  dimension: Dimension;
  unidadBase: UnidadBase;
  /** Unidad en la que se suele capturar la compra. */
  unidadCompraPreferida: string;
  /** Equivalencia declarada: 1 pieza = 250 g, 1 paquete = 500 g (Req. 5.6). */
  factorEquivalencia: FactorEquivalencia | null;

  // --- Estado de inventario ---------------------------------------------
  //
  // La separacion entre cantidad fisica y cantidad valuada es la que permite
  // aceptar existencias sin costo conocido sin que diluyan el promedio
  // ponderado (Req. 7.5, 8.4, 8.5).
  //
  // Estos tres campos son una CACHE de lectura rapida. La fuente de verdad es
  // la tabla inventoryMovements, y se pueden reconstruir desde ella (Req. 7.10).

  /** Cantidad fisica total disponible, en unidad base. Para saber si alcanza. */
  stockBase: number;
  /** Porcion de la cantidad fisica que tiene costo conocido. */
  stockValuadoBase: number;
  /** Valor monetario de la porcion valuada. */
  valorCentavos: Centavos;
  /**
   * Costo por unidad base de la ultima compra. Respaldo para costear cuando la
   * cantidad valuada esta en cero (Req. 10.6). No es un promedio: es el ultimo
   * dato real conocido.
   */
  ultimoCostoPorBase: number | null;

  /** Cantidad minima deseada, para senalar existencias bajas (Req. 5.8). */
  stockMinimoBase: number | null;
  /** Los insumos referenciados no se borran, se desactivan (Req. 5.9). */
  activo: boolean;
  creadoEn: string;
}

// ---------------------------------------------------------------------------
// 3. Compras — HISTORIAL INMUTABLE
// ---------------------------------------------------------------------------

export interface Purchase {
  id: string;
  fecha: FechaLocal;
  proveedor: string | null;
  totalCentavos: Centavos;
  notas: string | null;
  /** Id de la compra compensatoria que la anula (Req. 6.8). */
  anuladaPor: string | null;
  creadoEn: string;
}

export interface PurchaseItem {
  id: string;
  purchaseId: string;
  ingredientId: string;
  /** Cantidad tal como se capturo, para poder mostrar el renglon como se vio. */
  cantidadCaptura: number;
  unidadCaptura: string;
  /** Cantidad convertida a unidad base. Es la que opera. */
  cantidadBase: number;
  costoCentavos: Centavos;
}

// ---------------------------------------------------------------------------
// 4. Recetas
// ---------------------------------------------------------------------------

export interface Recipe {
  id: string;
  nombre: string;
  categoria: string | null;
  rendimientoPorciones: number;
  precioVentaCentavos: Centavos;
  instrucciones: string | null;
  /** Dias de anticipacion con que debe prepararse. Predeterminado 1 (Req. 9.9). */
  diasAnticipacion: number;
  activo: boolean;
  creadoEn: string;
}

export interface RecipeItem {
  id: string;
  recipeId: string;
  ingredientId: string;
  cantidadCaptura: number;
  unidadCaptura: string;
  cantidadBase: number;
  /**
   * false = cantidad fija que no cambia con las porciones (Req. 9.8).
   * Caso tipico: sal y especias.
   */
  escala: boolean;
  notas: string | null;
}

// ---------------------------------------------------------------------------
// 5. Menu: plantilla y realidad
// ---------------------------------------------------------------------------

export interface MenuTemplate {
  id: string;
  nombre: string;
  activo: boolean;
  creadoEn: string;
}

export interface MenuTemplateItem {
  id: string;
  templateId: string;
  diaSemana: DiaSemana;
  recipeId: string;
  porcionesDefault: number;
}

/**
 * Menu real, con fecha calendario concreta.
 *
 * Se separa de la plantilla a proposito: editar la plantilla no debe alterar
 * los menus de semanas ya generadas (Req. 11.4).
 */
export interface PlannedItem {
  id: string;
  /** Ancla del modelo: el dia en que se vende o entrega (Req. 12.1). */
  fechaVenta: FechaLocal;
  /**
   * Campo CALCULADO e indexado: fechaVenta menos diasAnticipacion.
   *
   * Se persiste solo para poder consultarlo por indice. Lo escribe unicamente
   * el repositorio, nunca la interfaz, para que las dos fechas no puedan
   * quedar inconsistentes.
   */
  fechaPrep: FechaLocal;
  recipeId: string;
  porcionesPlaneadas: number;
  diasAnticipacion: number;
  /** Precio congelado al agregar el platillo al menu (Req. 11.9). */
  precioVentaCentavos: Centavos;
  notas: string | null;
  /**
   * Cuando se decidio NO cocinarlo. Null o ausente = sigue en pie.
   *
   * Un platillo planeado que no se vendio quedaba avisando "atrasado" para
   * siempre. La unica salida era borrarlo del menu, pero eso borra tambien el
   * dato de que ese dia no hubo pedidos, que es lo que conviene recordar al
   * planear la semana siguiente.
   *
   * Descartar NO es borrar: el platillo se conserva, deja de aparecer en el
   * tablero y en produccion, y se puede volver a poner en pie.
   *
   * El campo es OPCIONAL a proposito: no esta indexado, asi que Dexie no
   * necesita una version nueva, y los registros anteriores simplemente no lo
   * traen. Ausente se lee igual que null.
   */
  descartadoEn?: string | null;
  creadoEn: string;
}

// ---------------------------------------------------------------------------
// 6. Produccion
// ---------------------------------------------------------------------------

export type EstadoProduccion = 'pendiente' | 'en_proceso' | 'completada' | 'cancelada';

export interface Production {
  id: string;
  plannedItemId: string;
  fechaPrep: FechaLocal;
  fechaVenta: FechaLocal;
  recipeId: string;
  porcionesAProducir: number;
  estado: EstadoProduccion;
  completadaEn: string | null;
  /**
   * Costo CONGELADO al completar la produccion (Req. 13.6, 13.7).
   *
   * Es lo que hace que el historial de ganancias sea estable: comprar mas caro
   * el mes siguiente no reescribe las ganancias de este mes.
   */
  costoTotalCentavos: Centavos | null;
  /** true si algun insumo se costeo con cantidades sin costo propio. */
  costoEstimado: boolean;
  /** true si algun insumo no tenia costo alguno del cual partir. */
  costoIncompleto: boolean;
  /** Id de la produccion de reversion, si se revirtio (Req. 13.12). */
  revertidaPor: string | null;
  creadoEn: string;
}

/** HISTORIAL INMUTABLE */
export interface ProductionItem {
  id: string;
  productionId: string;
  ingredientId: string;
  /** Cantidad que arrojo el escalado de la receta. */
  cantidadPlanBase: number;
  /** Cantidad realmente utilizada, ajustable antes de confirmar (Req. 13.8). */
  cantidadRealBase: number;
  /** Costo congelado reconocido para esta cantidad. */
  costoCentavos: Centavos;
  /** Costo por unidad base vigente al momento de producir. */
  costoPorBaseSnapshot: number;
  /** true si parte de la cantidad no tenia costo propio y se le imputo el promedio. */
  costoImputado: boolean;
}

// ---------------------------------------------------------------------------
// 7. Ventas — HISTORIAL INMUTABLE
// ---------------------------------------------------------------------------

export interface Sale {
  id: string;
  fecha: FechaLocal;
  plannedItemId: string | null;
  recipeId: string;
  productionId: string | null;
  porcionesVendidas: number;
  precioUnitarioCentavos: Centavos;
  ingresoCentavos: Centavos;
  /** Costo tomado de la produccion. null = no disponible (Req. 15.10). */
  costoCentavos: Centavos | null;
  /** Id de la venta que esta corrige (Req. 15.9). */
  corrigeA: string | null;
  notas: string | null;
  creadoEn: string;
}

export type DestinoSobrante = 'sobrante' | 'consumo_personal' | 'merma' | 'otro';

export const DESTINOS_SOBRANTE: Readonly<Record<DestinoSobrante, string>> = {
  sobrante: 'Sobrante',
  consumo_personal: 'Consumo personal',
  merma: 'Merma',
  otro: 'Otro',
};

export interface SaleDisposition {
  id: string;
  plannedItemId: string;
  productionId: string | null;
  /**
   * Venta a la que pertenece este destino.
   *
   * OPCIONAL por compatibilidad: los registros creados antes de que existiera
   * este campo no lo traen, y un respaldo viejo se importa igual. Cuando falta,
   * el destino se atribuye al lote y no a una captura concreta.
   *
   * Sin este vinculo, editar una de varias capturas del mismo platillo borraba
   * los sobrantes de las otras, porque el unico discriminante disponible era
   * `(plannedItemId, productionId)`, que es el mismo para todas.
   *
   * No esta indexado a proposito: la tabla es pequena por naturaleza y agregar
   * un indice obligaria a subir la version del esquema.
   */
  saleId?: string;
  tipo: DestinoSobrante;
  porciones: number;
  costoCentavos: Centavos | null;
  notas: string | null;
  creadoEn: string;
}

// ---------------------------------------------------------------------------
// 8. Libro de movimientos de inventario — HISTORIAL INMUTABLE
// ---------------------------------------------------------------------------

export type TipoMovimiento =
  | 'saldo_inicial'
  | 'compra'
  | 'consumo_produccion'
  | 'ajuste_manual'
  | 'merma'
  | 'reversion'
  | 'importacion';

export const TIPOS_MOVIMIENTO: Readonly<Record<TipoMovimiento, string>> = {
  saldo_inicial: 'Saldo inicial',
  compra: 'Compra',
  consumo_produccion: 'Consumo por produccion',
  ajuste_manual: 'Ajuste manual',
  merma: 'Merma',
  reversion: 'Reversion',
  importacion: 'Importacion',
};

export type RefMovimiento =
  | 'purchase'
  | 'production'
  | 'ajuste'
  | 'saldo_inicial'
  | 'import'
  | null;

/**
 * Libro de movimientos. FUENTE DE VERDAD del inventario (Req. 7.8).
 *
 * Tabla de solo agregar: nunca se modifica ni se borra un registro. Permite
 * reconstruir las existencias y el valor de cualquier insumo, lo que da un
 * camino de reparacion ante cualquier descuadre (Req. 7.10, 18.3).
 */
export interface InventoryMovement {
  id: string;
  ingredientId: string;
  tipo: TipoMovimiento;
  fecha: FechaLocal;
  /** Cantidad FISICA con signo, en unidad base. Positiva entra, negativa sale. */
  cantidadBase: number;
  /**
   * Cantidad del acervo VALUADO con signo, que puede diferir de la fisica.
   *
   * Ocurre con las existencias sin costo conocido: entran al acervo fisico pero
   * no al valuado. Al consumirlas, la salida fisica es mayor que la valuada.
   *
   * Guardar los dos movimientos por separado permite reconstruir el inventario
   * sumando el libro, sin tener que recalcular costos en cadena (Req. 7.10).
   */
  cantidadValuadaBase: number;
  /**
   * Movimiento del VALOR del acervo valuado, con signo. Es contabilidad de
   * acervo: cuadra contra la suma del libro.
   */
  valorCentavos: Centavos;
  /** false = cantidad sin costo conocido; no toca el acervo valuado (Req. 8.4). */
  afectaValuacion: boolean;
  /**
   * Costo economico reconocido para esta cantidad. Es lo que alimenta el
   * costeo de la produccion, y puede diferir de `valorCentavos` cuando se
   * consumen cantidades sin costo propio.
   */
  costoAplicadoCentavos: Centavos | null;
  /** true cuando parte del costo se imputo desde el promedio conocido. */
  costoImputado: boolean;
  refTipo: RefMovimiento;
  refId: string | null;
  motivo: string | null;
  creadoEn: string;
}

// ---------------------------------------------------------------------------
// 9. Marcado de la lista de compras
// ---------------------------------------------------------------------------

export interface ShoppingCheck {
  id: string;
  ingredientId: string;
  /** Identifica el periodo de la lista, para no confundir semanas distintas. */
  periodoClave: string;
  atendido: boolean;
  creadoEn: string;
}

// ---------------------------------------------------------------------------
// 10. Pedidos de clientes
// ---------------------------------------------------------------------------

/**
 * Un pedido de un cliente. MODULO ADITIVO E INDEPENDIENTE.
 *
 * Existe para responder la pregunta de la entrega: a quien le toca cada plato,
 * cuanto debe, si ya pago y si ya se lo llevo. Antes eso se apuntaba en las
 * notas del menu y se olvidaba.
 *
 * NO participa en el costeo ni en el inventario, a proposito:
 *
 *   - El ingreso del negocio nace SOLO en `sales`. `totalCentavos` de aqui es
 *     lo que el cliente DEBE, no ingreso registrado. Contarlo como ingreso
 *     daria dos cifras peleandose por el mismo dinero.
 *   - `recipeId` y `plannedItemId` son referencias de LECTURA. Un pedido nunca
 *     escribe en recetas, menu, producciones, ventas ni movimientos de
 *     inventario. Las porciones a cocinar siguen saliendo del menu.
 *
 * La cantidad de porciones del pedido y las del menu son datos distintos y
 * deliberadamente independientes: el menu es lo que se planea producir y el
 * pedido es lo que una persona aparto.
 */
export interface Order {
  id: string;
  /** Nombre tal como lo escribe la usuaria. No hay catalogo de clientes. */
  clienteNombre: string;
  /** Ancla del modulo: el dia en que hay que entregarlo. */
  fechaEntrega: FechaLocal;
  /** Receta pedida. Referencia de lectura para no escribir nombres a mano. */
  recipeId: string;
  /** Platillo del menu con el que se relaciona, si se quiso ligar. Opcional. */
  plannedItemId: string | null;
  porciones: number;
  /** Se sugiere el de la receta y se puede cambiar por pedido. */
  precioPorPorcionCentavos: Centavos;
  /**
   * Porciones por precio, CONGELADO al capturar (igual criterio que el precio
   * del menu). Si manana sube el precio del platillo, lo que este cliente debe
   * no cambia.
   */
  totalCentavos: Centavos;
  /**
   * Cancelar en lugar de borrar cuando el cliente se arrepiente: el registro se
   * conserva y deja de sumar en los totales del dia. Borrar se reserva para el
   * error de captura.
   */
  estado: EstadoPedido;
  estadoPago: EstadoPago;
  estadoEntrega: EstadoEntrega;
  /**
   * Venta que registro este pedido. Hoy siempre null: la etapa 1 no toca
   * ventas. El campo existe para no volver a migrar cuando se conecte.
   */
  saleId: string | null;
  /**
   * Dia en que el cliente pago. Null o ausente = todavia no paga.
   *
   * `estadoPago` dice SI pago; esto dice CUANDO. Hacen falta los dos, porque la
   * caja necesita saber en que semana entro el dinero: Juan paga cada quince
   * dias, asi que su cobro cae en una semana distinta a la de la entrega.
   *
   * Antes solo existia `actualizadoEn`, que sirve de bitacora pero se sobreescribe
   * con cualquier otra edicion del pedido, asi que no se puede usar como fecha de
   * cobro.
   *
   * El campo es OPCIONAL a proposito: no esta indexado, asi que Dexie no necesita
   * una version nueva por el, y los pedidos anteriores simplemente no lo traen.
   */
  fechaPago?: FechaLocal | null;
  notas: string | null;
  creadoEn: string;
  actualizadoEn: string;
}

// ---------------------------------------------------------------------------
// 10. Caja: el dinero de verdad
// ---------------------------------------------------------------------------

/**
 * Movimiento de dinero que NO se puede deducir de otra tabla.
 *
 * QUE VIVE AQUI Y QUE NO
 *
 * Los cobros se sacan de los pedidos pagados y las compras de los tickets. Nada de
 * eso se guarda aqui: se leeria dos veces y las dos copias podrian discrepar. Esta
 * tabla es solo para lo que hoy no existe en ninguna parte, que es sobre todo el
 * dinero de ELLA.
 *
 * POR QUE HACIA FALTA
 *
 * Al 4 de septiembre habia cobrado $6,400 y comprado $9,034.52 en insumos. La
 * diferencia, $2,634.52, la puso de su cartera y la aplicacion no tenia donde
 * registrarlo. Sin este dato es imposible contestar cuanto dinero suyo sigue
 * metido en el negocio, ni cuanto puede comprar sin volver a poner.
 *
 * EL MONTO SIEMPRE ES POSITIVO
 *
 * El signo lo decide el tipo (ver signoDe en src/domain/cash.ts). Guardar montos
 * negativos invitaria a capturar un gasto en positivo y que sumara en lugar de
 * restar.
 */
export interface CashMovement {
  id: string;
  fecha: FechaLocal;
  tipo: TipoMovimientoCaja;
  /** SIEMPRE positivo. Ver signoDe() en el dominio. */
  montoCentavos: Centavos;
  /** En que se gasto o por que entro. "Gas de la semana", "puse para el pollo". */
  concepto: string;
  /** Solo para los gastos. Lista abierta: se puede escribir una nueva. */
  categoria: string | null;
  notas: string | null;
  creadoEn: string;
}

// ---------------------------------------------------------------------------
// Mapa de tablas, usado por el sistema de respaldos
// ---------------------------------------------------------------------------

export interface MapaTablas {
  settings: Settings[];
  ingredients: Ingredient[];
  purchases: Purchase[];
  purchaseItems: PurchaseItem[];
  recipes: Recipe[];
  recipeItems: RecipeItem[];
  menuTemplates: MenuTemplate[];
  menuTemplateItems: MenuTemplateItem[];
  plannedItems: PlannedItem[];
  productions: Production[];
  productionItems: ProductionItem[];
  sales: Sale[];
  saleDispositions: SaleDisposition[];
  inventoryMovements: InventoryMovement[];
  shoppingChecks: ShoppingCheck[];
  orders: Order[];
  cashMovements: CashMovement[];
}

export type NombreTabla = keyof MapaTablas;

/**
 * Acceso dinamico al mapa de tablas.
 *
 * El respaldo recorre todas las tablas por nombre, y cada una tiene un tipo
 * distinto. TypeScript no puede seguir ese recorrido sin perder el tipado, asi
 * que la conversion se concentra en estos dos ayudantes en lugar de repetirse
 * en cada punto de uso. Fuera de aqui, el mapa se usa siempre con su tipo real.
 */
export function escribirTabla(
  mapa: MapaTablas,
  tabla: NombreTabla,
  registros: readonly unknown[],
): void {
  (mapa as unknown as Record<NombreTabla, readonly unknown[]>)[tabla] = registros;
}

/** Lee una tabla como registros genericos, para inspeccionar campos por nombre. */
export function leerTabla(
  mapa: MapaTablas,
  tabla: NombreTabla,
): ReadonlyArray<Record<string, unknown>> {
  const vista = mapa as unknown as Record<
    NombreTabla,
    ReadonlyArray<Record<string, unknown>> | undefined
  >;
  return vista[tabla] ?? [];
}

/**
 * Orden de las tablas para exportar e importar.
 *
 * Las tablas padre van antes que las hijas para que la validacion de
 * integridad referencial pueda revisarse en un solo recorrido.
 */
export const TABLAS: readonly NombreTabla[] = [
  'settings',
  'ingredients',
  'recipes',
  'recipeItems',
  'menuTemplates',
  'menuTemplateItems',
  'purchases',
  'purchaseItems',
  'plannedItems',
  'productions',
  'productionItems',
  'sales',
  'saleDispositions',
  'inventoryMovements',
  'shoppingChecks',
  // Al final: referencia recetas, platillos del menu y ventas, asi que sus
  // tablas padre ya quedaron indexadas cuando se revisa la integridad.
  'orders',
  // No referencia a nadie: son movimientos de dinero sueltos.
  'cashMovements',
];

export const ETIQUETAS_TABLA: Readonly<Record<NombreTabla, string>> = {
  settings: 'Configuracion',
  ingredients: 'Insumos',
  recipes: 'Recetas',
  recipeItems: 'Ingredientes de recetas',
  menuTemplates: 'Plantillas de menu',
  menuTemplateItems: 'Renglones de plantilla',
  purchases: 'Compras',
  purchaseItems: 'Renglones de compra',
  plannedItems: 'Menu planeado',
  productions: 'Producciones',
  productionItems: 'Insumos consumidos',
  sales: 'Ventas',
  saleDispositions: 'Destino de sobrantes',
  inventoryMovements: 'Movimientos de inventario',
  shoppingChecks: 'Lista de compras',
  orders: 'Pedidos',
  cashMovements: 'Movimientos de caja',
};
